/**
 * SecureEmbed Hosting Costs Page
 *
 * Live AWS Cost Explorer data with charts, period selection, drill-down,
 * collapsible service breakdown, and cost recommendations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, TrendingUp, TrendingDown, Calendar, RefreshCw, AlertCircle,
  Loader2, ChevronDown, ChevronRight, Lightbulb, X, ArrowLeft, Info,
  Maximize2,
} from 'lucide-react';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Cell, LabelList,
  ReferenceLine,
} from 'recharts';
import { costsApi } from '../services/api';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { MetricTooltip } from '../components/ui';

interface DailyCost {
  date: string;
  cost: number;
}

interface ServiceCost {
  service: string;
  cost: number;
}

interface CostsData {
  period: string;
  start_date: string;
  end_date: string;
  total_cost: number;
  daily_average: number;
  currency: string;
  daily_costs: DailyCost[];
  service_costs: ServiceCost[];
  forecast: {
    forecast_total: number | null;
    forecast_unit: string;
  };
  timestamp: string;
}

const PERIOD_OPTIONS = [
  { value: '7d', label: '7 Days' },
  { value: '14d', label: '14 Days' },
  { value: '30d', label: '30 Days' },
  { value: '60d', label: '60 Days' },
  { value: '90d', label: '90 Days' },
  { value: '180d', label: '6 Months' },
  { value: '365d', label: '1 Year' },
  { value: 'custom', label: 'Custom Range' },
];

const SERVICE_COLORS = [
  '#0369A1', '#374151', '#6B7280', '#9CA3AF',
  '#2563eb', '#3B82F6', '#60A5FA', '#93C5FD',
  '#059669', '#10B981', '#D97706', '#F59E0B',
  '#DC2626', '#EF4444', '#7C3AED', '#A78BFA',
  '#0891B2', '#06B6D4', '#65A30D', '#84CC16',
];

const SERVICE_LIMIT_OPTIONS: { value: number; label: string }[] = [
  { value: 5, label: 'Top 5' },
  { value: 10, label: 'Top 10' },
  { value: 15, label: 'Top 15' },
  { value: 20, label: 'Top 20' },
  { value: 0, label: 'All' },
];

/** Map AWS service names to descriptions relevant to the SecureEmbed platform. */
const SERVICE_DESCRIPTIONS: Record<string, string> = {
  'Amazon OpenSearch Service': 'Hosts the vector search engine used by all SecureEmbed portals for semantic search and embedding similarity queries. Indexes include medicine, food, academic, energy, education, and tertiary resource embeddings. Runs within the VPC with HNSW-based cosine similarity indexing.',
  'Amazon DynamoDB': 'NoSQL database storing structured portal data including medicine items, food interactions, education curricula, CRM contacts/organisations, audit logs, user access controls, and backup metadata. Uses on-demand billing with point-in-time recovery enabled on all tables.',
  'AWS Lambda': 'Serverless compute powering all dashboard and portal API endpoints including status, health, ingestion, backup/restore, user management, cost queries, and data browse operations. Each portal has its own set of Lambda handlers for CRUD and search operations.',
  'Amazon S3': 'Object storage hosting the static frontend builds for all portals (Management Dashboard, Energy, Food, Medicine, Education, Academic, Stakeholder), Terraform state files, data ingestion chunks, CodeBuild artifacts, and backup vault storage.',
  'Amazon API Gateway': 'REST API gateway providing authenticated HTTPS endpoints for all portal backends. Each portal has its own API Gateway with Cognito authorizer integration, CORS configuration, and rate limiting.',
  'Amazon Cognito': 'User authentication and authorization service managing separate user pools for the Management Dashboard and each portal. Handles user sign-up, sign-in, MFA, group-based access control (Admins, Monitors, Users), and JWT token issuance.',
  'Amazon CloudFront': 'CDN distribution serving the static portal frontends with edge caching, HTTPS termination, and custom error pages. Each portal has its own CloudFront distribution with S3 origin access identity.',
  'AWS CodeBuild': 'CI/CD build service executing Terraform plans and applies via the bootstrap pipeline. Used for infrastructure deployments across all 10 Terraform layers.',
  'Amazon CloudWatch': 'Monitoring and observability service collecting logs from all Lambda functions, API Gateways, and CodeBuild projects. Also stores custom metrics and alarms for resource health monitoring.',
  'AWS Key Management Service': 'Encryption key management for data at rest across DynamoDB tables, S3 buckets, OpenSearch domains, Secrets Manager, and SSM parameters.',
  'Amazon Bedrock': 'Foundation model service used for generating text embeddings via the Titan Text Embeddings V2 model. Used during data ingestion to create 1024-dimensional vectors for semantic search across all portal datastores.',
  'AWS Secrets Manager': 'Secure storage for sensitive configuration values including OpenSearch master credentials, API keys, and service account passwords.',
  'Amazon EC2': 'Elastic compute instances used for long-running data ingestion tasks (academic papers, bulk embedding generation) that exceed Lambda timeout limits. Typically t3.medium or t3.large instances with T3 Unlimited.',
  'AWS Backup': 'Managed backup service handling automated and on-demand backups of DynamoDB tables and S3 buckets across all portals, with configurable retention and vault storage.',
  'AWS Systems Manager': 'Parameter Store used for cross-layer configuration sharing between Terraform layers, storing environment-specific values like Cognito pool IDs, API endpoints, OpenSearch domain URLs, and index counts.',
  'Amazon Simple Notification Service': 'Notification service for alarm actions, deployment notifications, and backup completion events.',
  'AWS CloudFormation': 'Infrastructure as code service used alongside Terraform for certain AWS-native resource provisioning.',
  'Amazon Elastic Compute Cloud - Compute': 'EC2 compute instances for data ingestion and embedding generation workloads.',
  'EC2 - Other': 'EC2 ancillary costs including EBS volumes, elastic IPs, data transfer, and network interface charges.',
  'AWS Config': 'Configuration compliance service tracking resource configuration changes across the SecureEmbed account.',
  'Amazon Virtual Private Cloud': 'Network isolation layer providing private subnets for OpenSearch, NAT gateways for Lambda VPC access, VPC peering for cross-VPC communication, and security group management.',
};

const ENVIRONMENT = 'dev';

/** Generate a contextual description of what services contributed to the daily trend. */
function generateDailyTrendDescription(data: CostsData): string {
  const services = data.service_costs;
  if (services.length === 0) return '';

  const totalCost = services.reduce((s, sc) => s + sc.cost, 0);
  const topServices = services.slice(0, 5);
  const topPct = totalCost > 0 ? (topServices.reduce((s, sc) => s + sc.cost, 0) / totalCost * 100).toFixed(0) : '0';

  const descriptions: string[] = [];
  descriptions.push(`This daily trend reflects aggregate spending across ${services.length} AWS services over the selected period, totalling ${formatCurrency(totalCost)}.`);
  descriptions.push(`The top ${topServices.length} services account for ${topPct}% of all costs.`);

  for (const svc of topServices) {
    const pct = totalCost > 0 ? (svc.cost / totalCost * 100).toFixed(1) : '0.0';
    const desc = SERVICE_DESCRIPTIONS[svc.service];
    if (desc) {
      descriptions.push(`${svc.service} (${pct}%, ${formatCurrency(svc.cost)}): ${desc}`);
    } else {
      descriptions.push(`${svc.service} (${pct}%, ${formatCurrency(svc.cost)}): Standard AWS service used within the SecureEmbed ${ENVIRONMENT} environment.`);
    }
  }

  descriptions.push(`This environment (${ENVIRONMENT}) hosts the hub-and-spoke architecture where the Management Dashboard coordinates monitoring across all portal deployments. Costs reflect both the management layer infrastructure and shared services consumed by the dev/test/uat/prod spoke environments.`);

  return descriptions.join(' ');
}

const formatCurrency = (value: number): string => {
  return `$${value.toFixed(2)}`;
};

const formatDateLabel = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
};

// ---------------------------------------------------------------------------
// Chart helpers
// ---------------------------------------------------------------------------

/** Compute linear regression trendline values, returned as new array with `trend` field. */
function addTrendline<T extends { cost: number }>(data: T[]): (T & { trend: number })[] {
  if (data.length < 2) return data.map(d => ({ ...d, trend: d.cost }));
  const n = data.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += data[i].cost;
    sumXY += i * data[i].cost;
    sumX2 += i * i;
  }
  const denom = n * sumX2 - sumX * sumX;
  const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
  const intercept = (sumY - slope * sumX) / n;
  return data.map((d, i) => ({ ...d, trend: slope * i + intercept }));
}

/** Return only the dates where the month changes (for X-axis ticks). */
function getMonthChangeTicks(dailyCosts: DailyCost[]): string[] {
  const ticks: string[] = [];
  let lastMonth = '';
  for (const dc of dailyCosts) {
    const d = new Date(dc.date + 'T00:00:00');
    const month = d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
    if (month !== lastMonth) {
      ticks.push(dc.date);
      lastMonth = month;
    }
  }
  return ticks;
}

/** Format a date to month only (e.g. "Feb 26"). */
function formatMonthTick(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
}

/** Compute average of an array of costs. */
function computeAvg(data: { cost: number }[]): number {
  if (data.length === 0) return 0;
  return data.reduce((s, d) => s + d.cost, 0) / data.length;
}

// Generate cost recommendations from data — each with detailed analysis, risks, and research directions
function generateRecommendations(data: CostsData): { title: string; detail: string; type: 'info' | 'saving' | 'warning' }[] {
  const recs: { title: string; detail: string; type: 'info' | 'saving' | 'warning' }[] = [];
  const dailyCosts = data.daily_costs;
  const serviceCosts = data.service_costs;
  const totalCost = serviceCosts.reduce((s, sc) => s + sc.cost, 0);

  // Trend analysis
  if (dailyCosts.length >= 7) {
    const firstHalf = dailyCosts.slice(0, Math.floor(dailyCosts.length / 2));
    const secondHalf = dailyCosts.slice(Math.floor(dailyCosts.length / 2));
    const firstAvg = firstHalf.reduce((s, d) => s + d.cost, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((s, d) => s + d.cost, 0) / secondHalf.length;
    const changePct = firstAvg > 0 ? ((secondAvg - firstAvg) / firstAvg) * 100 : 0;

    if (changePct > 15) {
      recs.push({
        title: 'Costs Trending Upward',
        detail: `Daily costs have increased ${changePct.toFixed(1)}% in the second half of the period, moving from ${formatCurrency(firstAvg)}/day to ${formatCurrency(secondAvg)}/day. This upward trend could indicate new resources being provisioned, increased data ingestion activity, or higher API traffic across the portals. If this increase is unexpected, it warrants immediate investigation — check for unintended EC2 instances left running, OpenSearch domain scaling events, or large Bedrock embedding batches. Risk: If left unchecked, a sustained ${changePct.toFixed(0)}% increase compounds over time and could significantly exceed monthly budget projections. Even a modest daily increase of ${formatCurrency(secondAvg - firstAvg)} translates to an additional ${formatCurrency((secondAvg - firstAvg) * 30)}/month. Further research: Use the drill-down feature to click on high-cost days and identify which specific services drove the increase. Review CloudTrail logs for resource creation events during the acceleration period, and check if any scheduled data ingestion jobs have changed in scope.`,
        type: 'warning',
      });
    } else if (changePct < -15) {
      recs.push({
        title: 'Costs Trending Downward',
        detail: `Daily costs have decreased ${Math.abs(changePct).toFixed(1)}% in the second half of the period, falling from ${formatCurrency(firstAvg)}/day to ${formatCurrency(secondAvg)}/day. This positive trend suggests successful cost optimization, reduced workload activity, or resources being decommissioned. However, verify that the reduction is intentional rather than a sign of service degradation — lower costs from a stopped OpenSearch domain or disabled Lambda functions could mean portal features are broken. Risk: An unexpected cost drop may indicate silent failures. If data ingestion pipelines stopped running, embedding indexes could become stale, degrading search quality for end users without obvious errors. Further research: Confirm all portal health checks are passing and that scheduled ingestion jobs completed successfully. Compare the cost drop against CloudWatch metrics to verify that API request volumes and data processing throughput remain at expected levels.`,
        type: 'info',
      });
    } else {
      recs.push({
        title: 'Costs Stable — Predictable Spending',
        detail: `Daily costs are relatively stable with only a ${changePct > 0 ? '+' : ''}${changePct.toFixed(1)}% change between the first and second half of the period, averaging ${formatCurrency(data.daily_average)}/day. Stable costs are a positive indicator that the infrastructure is in a steady state with consistent usage patterns. The projected monthly run rate is approximately ${formatCurrency(data.daily_average * 30)}/month. This predictability makes it a good time to evaluate Reserved Instance or Savings Plan commitments, as the baseline is well-established. Risk: Stable costs can mask gradual inefficiencies — resources that were provisioned for peak loads but are consistently underutilised still appear as steady spending. Further research: Review individual service trends by clicking on each service bar to identify any that have remained flat but could be right-sized. Check OpenSearch instance types and DynamoDB provisioned capacity for over-provisioning opportunities.`,
        type: 'info',
      });
    }
  }

  // Top cost drivers
  if (serviceCosts.length > 0) {
    const topService = serviceCosts[0];
    const topPct = totalCost > 0 ? (topService.cost / totalCost) * 100 : 0;
    if (topPct > 50) {
      const svcDesc = SERVICE_DESCRIPTIONS[topService.service] || 'a core infrastructure service';
      recs.push({
        title: 'Single Service Dominates Costs',
        detail: `${topService.service} accounts for ${topPct.toFixed(1)}% of total costs (${formatCurrency(topService.cost)} out of ${formatCurrency(totalCost)}). This service is ${svcDesc.charAt(0).toLowerCase() + svcDesc.slice(1, 120)}. Having a single service represent more than half of all spending creates concentration risk — any pricing change or configuration drift for this service has an outsized impact on the overall budget. Review the service's configuration for optimization opportunities such as instance right-sizing, reserved capacity pricing, or architectural alternatives. Risk: Over-reliance on a single expensive service means a pricing increase, unexpected scaling event, or misconfiguration could cause budget overruns with little buffer. Additionally, if the service experiences an outage, it represents both the highest cost AND the highest business impact. Further research: Click on this service in the chart to view its daily cost trend and identify whether costs are stable or growing. Review the AWS Trusted Advisor console for service-specific recommendations, and consider whether workload distribution across alternative services could reduce concentration.`,
        type: 'saving',
      });
    }
  }

  // Spike detection
  if (dailyCosts.length >= 3) {
    const avg = data.daily_average;
    const spikes = dailyCosts.filter(d => d.cost > avg * 2 && d.cost > 1);
    if (spikes.length > 0) {
      const spikeDate = spikes[spikes.length - 1];
      const spikeMultiple = (spikeDate.cost / avg).toFixed(1);
      const totalSpikeExtra = spikes.reduce((s, d) => s + (d.cost - avg), 0);
      recs.push({
        title: 'Cost Spikes Detected',
        detail: `${spikes.length} day${spikes.length > 1 ? 's' : ''} exceeded 2x the daily average during this period. The most recent spike was on ${formatDateLabel(spikeDate.date)} at ${formatCurrency(spikeDate.cost)}, which is ${spikeMultiple}x the average of ${formatCurrency(avg)}. These spikes collectively added approximately ${formatCurrency(totalSpikeExtra)} in excess costs above the baseline. Common causes in the SecureEmbed environment include bulk data ingestion runs (Bedrock embedding batches), EC2 instances left running after ingestion tasks, OpenSearch index rebuilds, or one-time deployment activities through CodeBuild. Risk: Recurring unplanned spikes erode budget predictability and may indicate automated processes that scale without cost guardrails. If spikes correlate with data ingestion schedules, they may be expected — but if they appear random, they could signal runaway processes or misconfigured auto-scaling. Further research: Click on the spike days in the Daily Cost Trend chart to see the service-level breakdown. Cross-reference spike dates with deployment history and data ingestion logs. Consider setting up CloudWatch billing alarms at thresholds like ${formatCurrency(avg * 2)}/day to receive early warnings.`,
        type: 'warning',
      });
    }
  }

  // Idle resource hints
  const idleServices = serviceCosts.filter(s => s.cost > 0.01 && s.cost < 0.50);
  if (idleServices.length > 2) {
    const idleTotal = idleServices.reduce((s, sc) => s + sc.cost, 0);
    const idleNames = idleServices.map(s => s.service).slice(0, 5).join(', ');
    recs.push({
      title: 'Possible Idle or Underutilised Resources',
      detail: `${idleServices.length} services have very low but non-zero costs totalling ${formatCurrency(idleTotal)} for this period: ${idleNames}${idleServices.length > 5 ? `, and ${idleServices.length - 5} more` : ''}. While individually small, these costs add up to approximately ${formatCurrency(idleTotal / (dailyCosts.length || 1) * 365)}/year if left unchecked. Services with minimal usage often indicate resources provisioned during development or testing that were never cleaned up — unused NAT gateways, idle Elastic IPs, empty S3 buckets with versioning enabled, or CloudWatch log groups with retention but no new data. Risk: The primary risk is not the cost itself but the operational complexity — idle resources create noise in monitoring, expand the attack surface, and make it harder to understand the actual infrastructure footprint. They can also mask genuine low-level usage that should be monitored. Further research: For each low-cost service, determine whether it's actively serving a purpose. Check if any EC2 instances are stopped but still incurring EBS volume costs. Review S3 bucket lifecycle policies and consider enabling intelligent tiering for infrequently accessed data.`,
      type: 'saving',
    });
  }

  // Forecast comparison
  if (data.forecast.forecast_total != null && data.total_cost > 0) {
    const monthlyRate = data.daily_average * 30;
    const forecastDiff = data.forecast.forecast_total - monthlyRate;
    const forecastDiffPct = (forecastDiff / monthlyRate) * 100;
    const isExtremeOutlier = Math.abs(forecastDiffPct) > 500;
    if (isExtremeOutlier) {
      recs.push({
        title: 'AWS Forecast Unreliable — Using Linear Projection',
        detail: `AWS Cost Explorer forecasts ${formatCurrency(data.forecast.forecast_total)}/month, which is ${forecastDiffPct > 0 ? '+' : ''}${forecastDiffPct.toFixed(0)}% above your current spending rate of ${formatCurrency(monthlyRate)}/month. This extreme divergence is almost certainly caused by AWS's ML model overreacting to recent one-off usage spikes (such as Bedrock AI model invocations during development) on a low-spend account. The dashboard is showing the linear projection (${formatCurrency(monthlyRate)}/month) as the forecast instead, which is a more reliable estimate of ongoing costs. If you have genuinely increased your sustained usage, this correction will self-adjust as the new spending pattern stabilises over 7-14 days and the daily average rises accordingly.`,
        type: 'info',
      });
    } else if (Math.abs(forecastDiffPct) > 10) {
      recs.push({
        title: 'AWS Forecast Diverges from Current Rate',
        detail: `AWS Cost Explorer forecasts ${formatCurrency(data.forecast.forecast_total)}/month for the next 30 days, while your current spending rate projects ${formatCurrency(monthlyRate)}/month — a ${forecastDiffPct > 0 ? '+' : ''}${forecastDiffPct.toFixed(1)}% divergence. The AWS forecast uses machine learning models that account for historical patterns, seasonality, and recent trends, so a significant divergence from the simple linear projection may indicate that AWS has detected emerging patterns not yet visible in the daily averages. ${forecastDiffPct > 0 ? 'A higher forecast suggests AWS expects cost acceleration, possibly from scheduled reserved capacity renewals, upcoming data transfer increases, or service tier upgrades.' : 'A lower forecast suggests AWS expects cost deceleration, possibly from expiring short-term workloads or seasonal reduction in API traffic.'} Risk: ${forecastDiffPct > 20 ? 'A forecast more than 20% above current rate is a strong warning signal. If accurate, monthly costs could exceed budget thresholds. Consider whether any planned deployments, data migrations, or scaling events are driving this projection.' : 'Moderate forecast divergence is common and may self-correct. However, it is worth monitoring over the next 7-14 days to see if actual costs converge toward the forecast or the current rate.'} Further research: Compare the forecast against the previous month\'s actual spending. Review any upcoming scheduled tasks such as bulk data ingestion, infrastructure deployments, or environment provisioning that could explain the projected change. Consider enabling AWS Budgets with action triggers if not already configured.`,
        type: forecastDiffPct > 20 ? 'warning' : 'info',
      });
    }
  }

  // EC2/compute recommendation
  const ec2Cost = serviceCosts.find(s => s.service.includes('EC2') || s.service.includes('Elastic Compute'));
  if (ec2Cost && ec2Cost.cost > 10) {
    const ec2Pct = totalCost > 0 ? (ec2Cost.cost / totalCost * 100).toFixed(1) : '0.0';
    recs.push({
      title: 'EC2 Compute Cost Optimization Opportunity',
      detail: `EC2 costs are ${formatCurrency(ec2Cost.cost)} for this period, representing ${ec2Pct}% of total spending. In the SecureEmbed environment, EC2 is primarily used for data ingestion tasks that exceed Lambda's 15-minute timeout — such as bulk academic paper processing, medicine data imports, and large-scale embedding generation via Bedrock. If these instances run consistently, Reserved Instances (1-year or 3-year terms) could reduce costs by 30-40%, while Savings Plans offer similar discounts with more flexibility across instance types. For burst workloads like data ingestion, Spot Instances can save up to 90% but require fault-tolerant job design. Risk: Committing to Reserved Instances locks in spend regardless of actual usage — if workloads shift to Lambda or containerised alternatives, the reserved capacity becomes waste. Spot Instances carry interruption risk, which could corrupt partially-completed ingestion jobs if not handled with checkpointing. Additionally, right-sizing instances too aggressively (e.g., t3.small instead of t3.medium) can cause CPU credit exhaustion and job failures. Further research: Review EC2 instance utilisation in CloudWatch (CPU, memory, network) to determine if instances are right-sized. Check whether data ingestion jobs could be restructured to use Step Functions with Lambda for smaller batches. Evaluate AWS Graviton (ARM-based) instances which offer 20% better price-performance for compute-intensive workloads.`,
      type: 'saving',
    });
  }

  // OpenSearch recommendation
  const osCost = serviceCosts.find(s => s.service.includes('OpenSearch'));
  if (osCost && osCost.cost > 5) {
    const osPct = totalCost > 0 ? (osCost.cost / totalCost * 100).toFixed(1) : '0.0';
    recs.push({
      title: 'OpenSearch Domain Cost Review',
      detail: `OpenSearch Service costs are ${formatCurrency(osCost.cost)} for this period (${osPct}% of total). OpenSearch is the backbone of SecureEmbed's vector search capability, hosting embedding indexes for all portals with HNSW-based cosine similarity search. The primary cost drivers are instance type, number of data nodes, and storage volume. For a development environment, a single-node t3.small.search or t3.medium.search instance may be sufficient, while production would require multi-AZ deployment with dedicated master nodes. Consider whether the current instance type matches the actual query load — if search latency is acceptable and index sizes are modest, a smaller instance type could reduce costs significantly. Risk: Downsizing OpenSearch too aggressively can cause query timeouts during concurrent searches, index corruption during bulk operations, or out-of-memory errors when working with large embedding indexes. Multi-AZ removal saves costs but eliminates high availability — a single-node failure would cause complete search outage across all portals. Further research: Review OpenSearch slow logs and CloudWatch metrics for JVM memory pressure, CPU utilisation, and search latency percentiles. Consider using UltraWarm storage for older indexes that are queried infrequently. Evaluate whether OpenSearch Serverless could be more cost-effective for variable query workloads. Check if any indexes can be consolidated or if unused indexes should be deleted.`,
      type: 'saving',
    });
  }

  return recs;
}

// ---------------------------------------------------------------------------
// Pop-out chart modal
// ---------------------------------------------------------------------------
const ChartModal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
    <div
      className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-[1400px] max-h-[90vh] overflow-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Reusable daily trend chart (used for main Daily Cost Trend + drill-down service trend)
// ---------------------------------------------------------------------------
const DailyTrendChart: React.FC<{
  data: DailyCost[];
  height: number;
  barColor: string;
  showMonthOnlyTicks?: boolean;
  onClick?: (dayData: any) => void;
}> = ({ data, height, barColor, showMonthOnlyTicks, onClick }) => {
  const withTrend = addTrendline(data);
  const avg = computeAvg(data);
  const monthTicks = showMonthOnlyTicks ? getMonthChangeTicks(data) : undefined;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={withTrend}
        onClick={onClick ? (e) => e?.activePayload?.[0]?.payload && onClick(e.activePayload[0].payload) : undefined}
        style={onClick ? { cursor: 'pointer' } : undefined}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          ticks={monthTicks}
          tickFormatter={showMonthOnlyTicks ? formatMonthTick : formatDateLabel}
          tick={{ fontSize: 12 }}
          interval={showMonthOnlyTicks ? 0 : Math.max(0, Math.floor(data.length / 8) - 1)}
        />
        <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} />
        <RechartsTooltip
          formatter={(value: number, name: string) => {
            if (name === 'trend') return [formatCurrency(value), 'Trend'];
            return [formatCurrency(value), 'Cost'];
          }}
          labelFormatter={formatDateLabel}
        />
        <ReferenceLine
          y={avg}
          stroke="#DC2626"
          strokeDasharray="6 4"
          strokeWidth={1.5}
          label={{ value: `Avg ${formatCurrency(avg)}`, position: 'right', fill: '#DC2626', fontSize: 11 }}
        />
        <Bar dataKey="cost" fill={barColor} radius={[2, 2, 0, 0]} />
        <Line
          dataKey="trend"
          type="linear"
          stroke="#DC2626"
          strokeWidth={2}
          dot={false}
          activeDot={false}
          legendType="none"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Reusable horizontal service bar chart
// ---------------------------------------------------------------------------
const ServiceBarChart: React.FC<{
  data: ServiceCost[];
  totalCost: number;
  height: number;
  onClick?: (serviceData: any) => void;
}> = ({ data, totalCost, height, onClick }) => {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ right: 120 }}
        onClick={onClick ? (e) => e?.activePayload?.[0]?.payload && onClick(e.activePayload[0].payload) : undefined}
        style={onClick ? { cursor: 'pointer' } : undefined}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          type="number"
          tickFormatter={(v) => `$${v}`}
          tick={{ fontSize: 12 }}
        />
        <YAxis
          type="category"
          dataKey="service"
          width={180}
          tick={{ fontSize: 11 }}
          tickFormatter={(name: string) => name.length > 28 ? name.slice(0, 25) + '...' : name}
        />
        <RechartsTooltip
          formatter={(value: number) => {
            const pct = totalCost > 0 ? ((value / totalCost) * 100).toFixed(1) : '0.0';
            return [`${formatCurrency(value)} (${pct}%)`, 'Cost'];
          }}
        />
        <Bar dataKey="cost" radius={[0, 2, 2, 0]}>
          {data.map((_, index) => (
            <Cell key={index} fill={SERVICE_COLORS[index % SERVICE_COLORS.length]} />
          ))}
          <LabelList
            dataKey="cost"
            position="right"
            formatter={(value: number) => {
              const pct = totalCost > 0 ? ((value / totalCost) * 100).toFixed(1) : '0.0';
              return `$${value.toFixed(2)} (${pct}%)`;
            }}
            style={{ fontSize: 10, fill: '#374151' }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const CostsPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { environment } = useEnvironment();
  const [period, setPeriod] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState<CostsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableExpanded, setTableExpanded] = useState(false);
  const [poppedChart, setPoppedChart] = useState<'daily' | 'service' | 'drilldown' | null>(null);
  const [serviceLimit, setServiceLimit] = useState(10);

  // Drill-down state
  const [drillDown, setDrillDown] = useState<{
    type: 'day' | 'service';
    label: string;
    data: any[];
    loading: boolean;
  } | null>(null);

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDrillDown(null);
    try {
      if (period === 'custom' && customStart && customEnd) {
        const result = await costsApi.getCosts('30d', customStart, customEnd, environment);
        setData(result);
      } else if (period !== 'custom') {
        const result = await costsApi.getCosts(period, undefined, undefined, environment);
        setData(result);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to fetch cost data';
      setError(msg);
    } finally {
      setLoading(false);
    }
    // W1b: refetch when the global environment changes so costs are per-env.
  }, [period, customStart, customEnd, environment]);

  useEffect(() => {
    if (period === 'custom' && (!customStart || !customEnd)) return;
    fetchCosts();
  }, [fetchCosts]);

  const totalServiceCost = data?.service_costs?.reduce((sum, s) => sum + s.cost, 0) || 0;
  const displayedServices = serviceLimit === 0
    ? (data?.service_costs || [])
    : (data?.service_costs?.slice(0, serviceLimit) || []);
  const serviceLimitLabel = serviceLimit === 0 ? 'All' : `Top ${serviceLimit}`;
  const periodLabel = PERIOD_OPTIONS.find(p => p.value === period)?.label || period;

  // Forecast comparison: forecast vs current 30-day rate
  const currentMonthlyRate = data ? data.daily_average * 30 : 0;
  const forecastDelta = data?.forecast.forecast_total != null && currentMonthlyRate > 0
    ? ((data.forecast.forecast_total - currentMonthlyRate) / currentMonthlyRate) * 100
    : null;

  // When AWS forecast diverges >500% from the linear rate, it's almost certainly
  // overreacting to recent usage spikes on a low-spend account. Show the linear
  // projection instead to avoid misleading the user.
  const FORECAST_DIVERGENCE_THRESHOLD = 500;
  const forecastIsUnreliable = forecastDelta != null && Math.abs(forecastDelta) > FORECAST_DIVERGENCE_THRESHOLD;
  const displayedForecast = forecastIsUnreliable ? currentMonthlyRate : data?.forecast.forecast_total ?? null;

  // Handle drill-down: click a daily bar to see service breakdown
  const handleDayClick = async (dayData: any) => {
    if (!dayData?.date) return;
    setDrillDown({ type: 'day', label: dayData.date, data: [], loading: true });
    try {
      const result = await costsApi.getDailyServices(dayData.date);
      setDrillDown({ type: 'day', label: dayData.date, data: result.services || [], loading: false });
    } catch {
      setDrillDown({ type: 'day', label: dayData.date, data: [], loading: false });
    }
  };

  // Handle drill-down: click a service to see daily trend
  const handleServiceClick = async (serviceData: any) => {
    if (!serviceData?.service) return;
    setDrillDown({ type: 'service', label: serviceData.service, data: [], loading: true });
    try {
      const result = await costsApi.getServiceTrend(serviceData.service, period === 'custom' ? '30d' : period);
      setDrillDown({ type: 'service', label: serviceData.service, data: result.daily_costs || [], loading: false });
    } catch {
      setDrillDown({ type: 'service', label: serviceData.service, data: [], loading: false });
    }
  };

  const recommendations = data ? generateRecommendations(data) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {!embedded && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Hosting Costs</h2>
            <p className="text-gray-500 mt-1">Live AWS cost data from Cost Explorer.</p>
          </div>
        )}

        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <button
            onClick={fetchCosts}
            disabled={loading}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Custom Date Range */}
      {period === 'custom' && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>
          <button
            onClick={fetchCosts}
            disabled={!customStart || !customEnd || loading}
            className="px-4 py-2 bg-[#0369A1] text-white rounded-lg text-sm hover:bg-iflow-800 disabled:opacity-50"
          >
            Apply
          </button>
        </div>
      )}

      {/* Note — moved to top */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Note:</strong> Cost data is from AWS Cost Explorer and may have a 24-48 hour delay.
          Actual charges may vary based on usage and AWS pricing changes.
          {data?.timestamp && (
            <span className="ml-1">Last fetched: {new Date(data.timestamp).toLocaleString()}</span>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Failed to load cost data</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          <span className="ml-3 text-gray-500">Loading cost data...</span>
        </div>
      )}

      {/* Content */}
      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 flex items-center">
                    Total Spend ({periodLabel})
                    <MetricTooltip fieldKey="costs.total" />
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {formatCurrency(data.total_cost)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                <Calendar className="w-3 h-3 inline mr-1" />
                {data.start_date} to {data.end_date}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 flex items-center">
                    Daily Average
                    <MetricTooltip fieldKey="costs.dailyAverage" />
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {formatCurrency(data.daily_average)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Over {data.daily_costs.length} days
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 flex items-center">
                    30-Day Forecast
                    <MetricTooltip fieldKey="costs.forecast" />
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {displayedForecast != null
                      ? formatCurrency(displayedForecast)
                      : 'N/A'}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  forecastIsUnreliable ? 'bg-amber-100' : 'bg-purple-100'
                }`}>
                  <TrendingUp className={`w-6 h-6 ${
                    forecastIsUnreliable ? 'text-amber-600' : 'text-purple-600'
                  }`} />
                </div>
              </div>
              {forecastIsUnreliable ? (
                <div className="mt-2">
                  <p className="text-xs font-medium text-amber-600">
                    Based on daily average (AWS forecast of {formatCurrency(data.forecast.forecast_total!)} skewed by recent usage spikes)
                  </p>
                </div>
              ) : data.forecast.forecast_total != null && forecastDelta != null ? (
                <div className="flex items-center gap-1.5 mt-2">
                  {forecastDelta > 0 ? (
                    <TrendingUp className="w-3.5 h-3.5 text-red-500" />
                  ) : forecastDelta < 0 ? (
                    <TrendingDown className="w-3.5 h-3.5 text-green-500" />
                  ) : null}
                  <p className={`text-xs font-medium ${
                    forecastDelta > 5 ? 'text-red-600' : forecastDelta < -5 ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {forecastDelta > 0 ? '+' : ''}{forecastDelta.toFixed(1)}% vs current rate ({formatCurrency(currentMonthlyRate)}/mo)
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-2">
                  {data.forecast.forecast_total != null
                    ? 'AWS Cost Explorer projection'
                    : 'Insufficient data for forecast'}
                </p>
              )}
            </div>
          </div>

          {/* Drill-Down Panel */}
          {drillDown && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDrillDown(null)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ArrowLeft className="w-4 h-4 text-gray-500" />
                  </button>
                  <h3 className="font-semibold text-gray-900">
                    {drillDown.type === 'day'
                      ? `Service Breakdown for ${formatDateLabel(drillDown.label)}`
                      : `Daily Trend: ${drillDown.label}`}
                  </h3>
                </div>
                <div className="flex items-center gap-1">
                  {drillDown.type === 'service' && drillDown.data.length > 0 && (
                    <button
                      onClick={() => setPoppedChart('drilldown')}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Expand chart"
                    >
                      <Maximize2 className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                  <button onClick={() => setDrillDown(null)} className="p-1 hover:bg-gray-100 rounded">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              {drillDown.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                  <span className="ml-2 text-gray-500 text-sm">Loading drill-down data...</span>
                </div>
              ) : drillDown.data.length === 0 ? (
                <p className="text-gray-400 text-center py-12 text-sm">No data available</p>
              ) : drillDown.type === 'day' ? (
                <ResponsiveContainer width="100%" height={Math.max(200, drillDown.data.length * 32)}>
                  <BarChart data={drillDown.data} layout="vertical" margin={{ right: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="service"
                      width={200}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(name: string) => name.length > 30 ? name.slice(0, 27) + '...' : name}
                    />
                    <RechartsTooltip formatter={(value: number) => [formatCurrency(value), 'Cost']} />
                    <Bar dataKey="cost" radius={[0, 2, 2, 0]}>
                      {drillDown.data.map((_: any, index: number) => (
                        <Cell key={index} fill={SERVICE_COLORS[index % SERVICE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <DailyTrendChart
                  data={drillDown.data}
                  height={300}
                  barColor="#2563eb"
                  showMonthOnlyTicks={drillDown.data.length > 14}
                />
              )}
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Cost Trend */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-gray-900">Daily Cost Trend</h3>
                {data.daily_costs.length > 0 && (
                  <button
                    onClick={() => setPoppedChart('daily')}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Expand chart"
                  >
                    <Maximize2 className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-4">Click a bar to see service breakdown for that day</p>
              {data.daily_costs.length > 0 ? (
                <>
                  <DailyTrendChart
                    data={data.daily_costs}
                    height={300}
                    barColor="#0369A1"
                    showMonthOnlyTicks={data.daily_costs.length > 14}
                    onClick={handleDayClick}
                  />
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">What This Covers</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      {generateDailyTrendDescription(data)}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-gray-400 text-center py-16">No daily cost data available</p>
              )}
            </div>

            {/* Cost by Service */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-gray-900">Cost by Service ({serviceLimitLabel})</h3>
                  <select
                    value={serviceLimit}
                    onChange={(e) => setServiceLimit(Number(e.target.value))}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#0369A1]"
                  >
                    {SERVICE_LIMIT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                {displayedServices.length > 0 && (
                  <button
                    onClick={() => setPoppedChart('service')}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Expand chart"
                  >
                    <Maximize2 className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-4">Click a service to see its daily trend</p>
              {displayedServices.length > 0 ? (
                <ServiceBarChart
                  data={displayedServices}
                  totalCost={totalServiceCost}
                  height={Math.max(300, displayedServices.length * 28)}
                  onClick={handleServiceClick}
                />
              ) : (
                <p className="text-gray-400 text-center py-16">No service cost data available</p>
              )}
            </div>
          </div>

          {/* Cost Analysis & Recommendations */}
          {recommendations.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-gray-900">Cost Analysis & Recommendations</h3>
              </div>
              <div className="p-6 space-y-4">
                {recommendations.map((rec, i) => (
                  <div key={i} className={`rounded-lg p-4 flex items-start gap-3 ${
                    rec.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                    rec.type === 'saving' ? 'bg-green-50 border border-green-200' :
                    'bg-blue-50 border border-blue-200'
                  }`}>
                    {rec.type === 'warning' ? (
                      <TrendingUp className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    ) : rec.type === 'saving' ? (
                      <TrendingDown className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${
                        rec.type === 'warning' ? 'text-amber-800' :
                        rec.type === 'saving' ? 'text-green-800' :
                        'text-blue-800'
                      }`}>{rec.title}</p>
                      <p className={`text-sm mt-1 ${
                        rec.type === 'warning' ? 'text-amber-700' :
                        rec.type === 'saving' ? 'text-green-700' :
                        'text-blue-700'
                      }`}>{rec.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Breakdown Table — Collapsible */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <button
              onClick={() => setTableExpanded(!tableExpanded)}
              className="w-full p-6 border-b border-gray-100 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h3 className="font-semibold text-gray-900">Service Breakdown ({data.service_costs.length} services)</h3>
              {tableExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {tableExpanded && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cost (USD)
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        % of Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.service_costs.map((item) => (
                      <tr key={item.service} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.service}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(item.cost)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {totalServiceCost > 0 ? ((item.cost / totalServiceCost) * 100).toFixed(1) : '0.0'}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">Total</td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900 text-right">
                        {formatCurrency(totalServiceCost)}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900 text-right">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Pop-out Modals */}
          {poppedChart === 'daily' && (
            <ChartModal title="Daily Cost Trend" onClose={() => setPoppedChart(null)}>
              <DailyTrendChart
                data={data.daily_costs}
                height={560}
                barColor="#0369A1"
                showMonthOnlyTicks={data.daily_costs.length > 14}
                onClick={handleDayClick}
              />
            </ChartModal>
          )}
          {poppedChart === 'service' && (
            <ChartModal title={`Cost by Service (${serviceLimitLabel})`} onClose={() => setPoppedChart(null)}>
              <ServiceBarChart
                data={displayedServices}
                totalCost={totalServiceCost}
                height={Math.max(400, displayedServices.length * 48)}
                onClick={handleServiceClick}
              />
            </ChartModal>
          )}
          {poppedChart === 'drilldown' && drillDown?.type === 'service' && drillDown.data.length > 0 && (
            <ChartModal title={`Daily Trend: ${drillDown.label}`} onClose={() => setPoppedChart(null)}>
              <DailyTrendChart
                data={drillDown.data}
                height={560}
                barColor="#2563eb"
                showMonthOnlyTicks={drillDown.data.length > 14}
              />
            </ChartModal>
          )}
        </>
      )}
    </div>
  );
};

export default CostsPage;
